/**
 * Format Composable
 * 數字格式化工具
 *
 * 架構：
 * - 單例模式：使用 nuxtApp._formatContext 避免 storeToRefs 重複創建
 */

import numbro from 'numbro'
import { storeToRefs } from 'pinia'

import { useSiteConfigStore } from '@/stores/siteConfig'
import { AMOUNT_KIND, composeCurrency, isThousandUnitCurrency, resolveCurrencyMeta } from '@/utils/currency'

const NUMBER_FORMATS = {
  INTEGER: {
    thousandSeparated: true,
    mantissa: 0,
  },
  FORCE_FLOAT: {
    thousandSeparated: true,
    roundingFunction: Math.floor,
    mantissa: 2,
  },
  FLOAT_FLOOR: {
    thousandSeparated: true,
    roundingFunction: Math.floor,
    trimMantissa: true,
    mantissa: 2,
  },
  FLOAT: {
    thousandSeparated: true,
    roundingFunction: Math.ceil,
    trimMantissa: true,
    mantissa: 2,
  },
  PERCENT: {
    trimMantissa: true,
    mantissa: 3,
  },
  HUMAN: {
    roundingFunction: Math.floor,
    trimMantissa: true,
    average: true,
    mantissa: 2,
  },
  RANK: {
    roundingFunction: Math.floor,
    average: true,
    mantissa: 2,
  },
  JACKPOT: {
    thousandSeparated: true,
    roundingFunction: Math.floor,
    mantissa: 2,
  },
  REWARD: {
    trimMantissa: true,
    average: true,
    mantissa: 3,
  },
  CRYPTO: {
    roundingFunction: Math.floor,
    thousandSeparated: true,
    trimMantissa: true,
    mantissa: 10,
  },
  CRYPTO_WAY: {
    roundingFunction: Math.floor,
    thousandSeparated: true,
    trimMantissa: true,
    mantissa: 4,
  },
  POINTS: {
    thousandSeparated: true,
    roundingFunction: Math.floor,
    trimMantissa: true,
    average: true,
    mantissa: 3,
  },
}

export const useFormat = () => {
  const nuxtApp = useNuxtApp()

  // 單例模式：確保 storeToRefs 只創建一次，避免 SSR 環境下記憶體洩漏
  if (!nuxtApp._formatContext) {
    const siteConfigStore = useSiteConfigStore()
    const { balancePrecision, siteConfig } = storeToRefs(siteConfigStore)
    const { currencyUnit, currencySymbol } = useRuntimeConfig().public

    const getFormatted = (data, formatName) => {
      const parseData = data || 0
      const format = NUMBER_FORMATS[formatName] ? { ...NUMBER_FORMATS[formatName] } : formatName

      if (numbro.unformat(parseData) === undefined) {
        return parseData
      }

      if (format === undefined) {
        return numbro(parseData).format({ thousandSeparated: true })
      }

      if (formatName === 'FORCE_FLOAT' || formatName === 'FLOAT_FLOOR' || formatName === 'FLOAT') {
        format.mantissa = parseInt(balancePrecision.value) || 2
      }

      if (!!format?.average && format?.average === true) {
        return numbro(parseData)
          .format({
            ...format,
            average: formatName === 'POINTS' ? parseData >= 10000 : parseData >= 1000,
          })
          .toUpperCase()
      } else {
        return numbro(parseData).format(format)
      }
    }

    const getCurrencyMeta = () => resolveCurrencyMeta(siteConfig.value, { unit: currencyUnit, symbol: currencySymbol })

    const getCurrencySymbol = () => getCurrencyMeta().symbol

    const isThousandUnitSite = () => isThousandUnitCurrency(siteConfig.value?.currency)

    /**
     * 格式化金額並依站台設定補上幣別符號。全站金額顯示的唯一入口。
     *
     * @param {number|string} value 金額
     * @param {string} [format] getFormatted 的 preset 名稱，不傳為預設千分位
     * @param {Object} [options]
     * @param {string} [options.kind] AMOUNT_KIND，預設 FIAT
     * @param {string} [options.unit] kind 為 CRYPTO 時的幣種；POINTS 需要文字單位時亦可傳
     * @param {boolean} [options.isolate] 是否以 LRI/PDI 隔離（要塞進 i18n 句子時必須）
     * @param {boolean} [options.hideCurrency] 明確只要數字
     * @returns {string}
     */
    const formatAmount = (value, format, options = {}) => {
      const { kind = AMOUNT_KIND.FIAT, unit = '', isolate = false, hideCurrency = false } = options
      const formatted = getFormatted(value, format)

      if (hideCurrency) return formatted

      // 加密貨幣與點數不查 supportCurrency：後端不提供加密幣別，點數也不掛法幣符號。
      // 兩者一律後綴該筆資料自帶的單位。
      if (kind === AMOUNT_KIND.CRYPTO || kind === AMOUNT_KIND.POINTS) {
        return unit ? composeCurrency(formatted, { symbol: unit, position: 'back', space: true }, isolate) : formatted
      }

      return composeCurrency(formatted, getCurrencyMeta(), isolate)
    }

    /**
     * 把幣別符號掛到「已經組好的字串」上，依站台 position 決定前後。
     * 用於金額區間（100 - 50,000 K₫）與後端直接給的原始值（如匯率），
     * 這些不該再經過 getFormatted。一般金額請用 formatAmount。
     *
     * @param {string|number} text 已組好的字串
     * @param {boolean} [isolate] 是否以 LRI/PDI 隔離
     * @returns {string}
     */
    const withCurrency = (text, isolate = false) => composeCurrency(String(text ?? ''), getCurrencyMeta(), isolate)

    // cleanup 方法：供 SSR cleanup plugin 調用（此 composable 無 watch 需清理）
    const cleanup = () => {}

    nuxtApp._formatContext = {
      getFormatted,
      formatAmount,
      withCurrency,
      getCurrencyMeta,
      getCurrencySymbol,
      isThousandUnitSite,
      cleanup,
    }
  }

  return nuxtApp._formatContext
}

/**
 * 將小數轉換為百分比字串，修正 JS 浮點運算精度問題
 * @param {number|string} value - 小數值（例：0.011）
 * @param {number} precision - 小數位數上限，預設 2
 * @returns {string} 百分比字串（例：'1.1%'），非數字輸入回傳 '0%'
 */
export const decimalToPercent = (value, precision = 2) => {
  const num = parseFloat(value)
  if (isNaN(num)) return '0%'
  return `${parseFloat((num * 100).toFixed(precision))}%`
}
